import { lazy, Suspense, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "@remix-run/react";
import { MapPin, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { AMENITIES, OFFICE_TYPES } from "~/components/blocks/Geosearch/constants";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";
import {
  type LocationLevel,
  type ParentLocationBrowseTarget,
  hasRealAreaSummary,
  isSafeInternalPath,
  resolveAreaSearchParentLocationBrowse,
  resolveLocationBrowseHref,
  resolvePropertyEndOfListNavigation,
} from "~/components/blocks/Geosearch/locationGeosearch";

import { EmptyLazyFallback, loadLazyModule } from "~/lib/helpers/resolveLazyModule";

const NoResultEnquireCard = lazy(() =>
  loadLazyModule(
    () => import("~/components/blocks/Geosearch/NoResultEnquireCard"),
    EmptyLazyFallback,
  ),
);

type ActiveFilterChip = {
  id: string;
  label: string;
  value: string;
  resetPayload: Record<string, string>;
};

type NoResultsProps = {
  query: string;
  params: { city?: string; state?: string; country?: string };
  parsedQuery?: Record<string, string>;
  callGeoSearch?: (options: Record<string, string>) => void;
  areaSummary?: any;
  isMobile?: boolean;
  isPropertyPage?: boolean;
  locationBasePath?: string;
  locationLevel?: LocationLevel;
};

function readParam(
  searchParams: URLSearchParams,
  queryParams: URLSearchParams,
  parsedQuery: Record<string, string> | undefined,
  key: string,
) {
  const fromUrl = searchParams.get(key);
  if (fromUrl && fromUrl !== "reset") return fromUrl;
  const fromParsed = parsedQuery?.[key];
  if (fromParsed && fromParsed !== "reset") return String(fromParsed);
  const fromQuery = queryParams.get(key);
  if (fromQuery && fromQuery !== "reset") return fromQuery;
  return "";
}

function titleCase(value: string) {
  return value
    .split(/[\s_+-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatLocationPart(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed.length <= 3) return trimmed.toUpperCase();
  return titleCase(trimmed);
}

function humanizeKey(value: string) {
  return titleCase(value.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " "));
}

function formatMoney(value: number, currencySymbol: string) {
  return `${currencySymbol}${Math.round(value).toLocaleString("en-US")}`;
}

function csvValues(value: string) {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

export default function NoResults({
  query,
  params,
  parsedQuery,
  callGeoSearch,
  areaSummary,
  isMobile,
  isPropertyPage = false,
  locationBasePath,
  locationLevel,
}: NoResultsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currencySymbol } = useCurrentCurrency();
  const onAreaPage = hasRealAreaSummary(areaSummary);

  const queryParams = useMemo(
    () => new URLSearchParams(query.startsWith("?") ? query.slice(1) : query),
    [query],
  );

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    const getFilterValue = (key: string) =>
      readParam(searchParams, queryParams, parsedQuery, key);

    const chips: ActiveFilterChip[] = [];
    const officeTypeReset: Record<string, string> = onAreaPage
      ? { workspaceType: "All", officeTypeFilterTouched: "1" }
      : { workspaceType: "reset" };

    const workspaceType = getFilterValue("workspaceType");
    if (workspaceType && workspaceType !== "All") {
      const labels = csvValues(workspaceType).map((token) => {
        const match = OFFICE_TYPES.find((type) => type.value === token.toLowerCase());
        return match?.label || humanizeKey(token);
      });
      if (labels.length) {
        chips.push({
          id: "officeType",
          label: "Office Type",
          value: labels.join(", "),
          resetPayload: officeTypeReset,
        });
      }
    }

    const minPrice = getFilterValue("minPrice");
    const maxPrice = getFilterValue("maxPrice");
    const minPriceNum = minPrice ? Number(minPrice) : NaN;
    const maxPriceNum = maxPrice ? Number(maxPrice) : NaN;
    const hasMinPrice = Number.isFinite(minPriceNum) && minPriceNum > 0;
    const hasMaxPrice = Number.isFinite(maxPriceNum) && maxPriceNum > 0;
    if (hasMinPrice || hasMaxPrice) {
      let value = "";
      if (hasMinPrice && hasMaxPrice) {
        value = `${formatMoney(minPriceNum, currencySymbol)} - ${formatMoney(maxPriceNum, currencySymbol)}`;
      } else if (hasMinPrice) {
        value = `${formatMoney(minPriceNum, currencySymbol)}+`;
      } else {
        value = `Up to ${formatMoney(maxPriceNum, currencySymbol)}`;
      }
      chips.push({
        id: "budget",
        label: "Budget",
        value,
        resetPayload: { minPrice: "reset", maxPrice: "reset", priceTiers: "reset" },
      });
    }

    const minPeople = getFilterValue("minPeople");
    const maxPeople = getFilterValue("maxPeople");
    const minPeopleNum = minPeople ? Number(minPeople) : NaN;
    const maxPeopleNum = maxPeople ? Number(maxPeople) : NaN;
    const hasMinPeople = Number.isFinite(minPeopleNum) && minPeopleNum > 0;
    const hasMaxPeople = Number.isFinite(maxPeopleNum) && maxPeopleNum > 0;
    if (hasMinPeople || hasMaxPeople) {
      let value = "";
      if (hasMinPeople && hasMaxPeople) {
        value = `${minPeopleNum} - ${maxPeopleNum} desks`;
      } else if (hasMinPeople) {
        value = `${minPeopleNum}+ desks`;
      } else {
        value = `Up to ${maxPeopleNum} desks`;
      }
      chips.push({
        id: "desks",
        label: "Desks",
        value,
        resetPayload: { minPeople: "reset", maxPeople: "reset" },
      });
    }

    const textValue = (
      searchParams.get("text") ||
      searchParams.get("q") ||
      getFilterValue("text") ||
      getFilterValue("q") ||
      ""
    ).trim();
    const locationParts = [
      textValue,
      formatLocationPart(searchParams.get("city") || ""),
      formatLocationPart(searchParams.get("state") || ""),
    ].filter(Boolean);
    const uniqueLocation = [...new Set(locationParts)];
    if (uniqueLocation.length) {
      chips.push({
        id: "location",
        label: "Location",
        value: uniqueLocation.join(", "),
        resetPayload: {
          city: "reset",
          state: "reset",
          text: "reset",
          q: "reset",
          latitude: "reset",
          longitude: "reset",
          minLatitude: "reset",
          maxLatitude: "reset",
          minLongitude: "reset",
          maxLongitude: "reset",
        },
      });
    }

    const amenitiesValue = getFilterValue("amenities") || getFilterValue("Amenities");
    const amenityLabels = csvValues(amenitiesValue).map((token) => {
      const match = AMENITIES.find((amenity) => amenity.value === token);
      return match?.label || humanizeKey(token);
    });
    if (amenityLabels.length) {
      chips.push({
        id: "amenities",
        label: "Amenities",
        value: amenityLabels.join(", "),
        resetPayload: { amenities: "reset", Amenities: "reset" },
      });
    }

    const facilitiesValue = getFilterValue("facilities") || getFilterValue("Facilities");
    const facilityLabels = csvValues(facilitiesValue).map(humanizeKey);
    if (facilityLabels.length) {
      chips.push({
        id: "facilities",
        label: "Facilities",
        value: facilityLabels.join(", "),
        resetPayload: { facilities: "reset", Facilities: "reset" },
      });
    }

    const promo = getFilterValue("promo") || getFilterValue("sale");
    if (promo) {
      chips.push({
        id: "promo",
        label: "Offer",
        value: "On Sale",
        resetPayload: { promo: "reset", sale: "reset" },
      });
    }

    const priceTiers = getFilterValue("priceTiers");
    if (priceTiers && priceTiers !== "none") {
      chips.push({
        id: "priceTiers",
        label: "Price tier",
        value: csvValues(priceTiers).join(", "),
        resetPayload: { priceTiers: "reset" },
      });
    }

    return chips;
  }, [currencySymbol, onAreaPage, parsedQuery, queryParams, searchParams]);

  const browseTarget = useMemo<ParentLocationBrowseTarget>(() => {
    if (
      isPropertyPage &&
      locationBasePath &&
      locationLevel &&
      params.country &&
      !onAreaPage
    ) {
      return resolvePropertyEndOfListNavigation({
        country: params.country,
        level: locationLevel,
        locationBasePath,
        stateSlug: params.state,
      });
    }

    return resolveAreaSearchParentLocationBrowse({
      country: params.country ?? "",
      urlCity: searchParams.get("city") || "",
      urlState: searchParams.get("state") || "",
      urlText: searchParams.get("text") || searchParams.get("q") || "",
      isCountryScope: searchParams.get("scope") === "country",
      urlHasPoint: Boolean(
        searchParams.get("latitude") || searchParams.get("longitude"),
      ),
      queryHasLatitude: query.includes("latitude"),
      paramsCity: params.city,
      paramsState: params.state,
      parsedCity: parsedQuery?.city,
      parsedState: parsedQuery?.state,
      areaCity: areaSummary?.city,
      areaSuburb: areaSummary?.suburb,
      areaState: areaSummary?.state,
      hasAreaSummary: onAreaPage,
    });
  }, [
    areaSummary?.city,
    areaSummary?.state,
    areaSummary?.suburb,
    isPropertyPage,
    locationBasePath,
    locationLevel,
    onAreaPage,
    params.city,
    params.country,
    params.state,
    parsedQuery?.city,
    parsedQuery?.state,
    query,
    searchParams,
  ]);

  const applyFilterPayload = (payload: Record<string, string>) => {
    if (callGeoSearch) {
      callGeoSearch(payload);
      return;
    }
    const next = new URLSearchParams(location.search);
    Object.keys(payload).forEach((key) => next.delete(key));
    const search = next.toString();
    navigate(search ? `${location.pathname}?${search}` : location.pathname);
  };

  const handleResetQueryParams = () => {
    if (callGeoSearch) {
      callGeoSearch({ reset: "true" });
      return;
    }
    navigate(location.pathname);
  };

  const handleAllLocations = () => {
    if (onAreaPage && callGeoSearch && browseTarget.areaPayload) {
      callGeoSearch(browseTarget.areaPayload);
      return;
    }

    const href = resolveLocationBrowseHref(
      browseTarget.href,
      params.country,
      browseTarget.text,
    );
    if (href && isSafeInternalPath(href)) {
      navigate(href);
    }
  };

  const showActiveFilters = activeFilters.length > 0;
  const allLocationsLabel = browseTarget.text;

  return (
    <div className="mx-auto flex w-full max-w-[760px] flex-col items-center justify-center gap-4 py-0 md:pt-2">
      <div className="flex w-full flex-col items-center justify-center text-center">
        <div className="flex items-center gap-6 text-left">
          <img
            src="/assets/misc/no-results.svg"
            alt="No result"
            width={110}
            height={110}
            className="h-[68px] w-[68px] shrink-0 object-contain md:h-[110px] md:w-[110px]"
          />
          <div>
            <p className="mb-2 text-base font-semibold text-oh-black md:text-2xl">
              Oops! No exact matches here.
            </p>
            <p className="mt-0 text-xs leading-relaxed text-oh-gray md:mt-2 md:text-sm">
              We couldn't find an exact match, but there are{" "}
              <span className="font-bold text-oh-black">35,000+ properties</span>{" "}
              to explore. Try broadening your location or adjusting your filters.
            </p>
          </div>
        </div>

        {showActiveFilters ? (
          <div className="mt-5 hidden w-full rounded-lg border border-gray-200 bg-oh-light-gray p-4 text-left md:block">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-oh-black">Active Filters</p>
              <Button
                type="button"
                variant="ghost"
                className="h-8 px-3 text-sm text-oh-secondary"
                onClick={handleResetQueryParams}
              >
                Reset All ({activeFilters.length})
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeFilters.map((chip) => (
                <span
                  key={chip.id}
                  className="inline-flex max-w-full items-center gap-2 rounded-full border border-oh-primary/50 bg-oh-lightTeal px-3 py-1.5 text-sm text-oh-dark-primary"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium">{chip.label}:</span> {chip.value}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${chip.label} filter`}
                    className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-oh-primary text-white hover:bg-oh-dark-primary"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      applyFilterPayload(chip.resetPayload);
                    }}
                  >
                    <X className="h-3 w-3" strokeWidth={3} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {showActiveFilters || allLocationsLabel ? (
          <div className="mt-6 flex w-full items-center gap-2">
            {showActiveFilters ? (
              <Button
                type="button"
                variant="outline"
                className="h-10 shrink-0 rounded-xl px-3 text-xs text-oh-secondary md:hidden md:text-sm"
                onClick={handleResetQueryParams}
              >
                Reset All ({activeFilters.length})
              </Button>
            ) : null}
            {allLocationsLabel ? (
              <Button
                type="button"
                variant="outline"
                className="group inline-flex min-w-0 flex-1 items-center gap-1.5 rounded-xl text-xs text-oh-black md:text-sm"
                onClick={handleAllLocations}
              >
                <MapPin className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                <span className="truncate underline-offset-2 group-hover:underline">
                  Browse all {allLocationsLabel} locations
                </span>
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Suspense fallback={null}>
        <NoResultEnquireCard country={params.country} isMobile={isMobile} />
      </Suspense>
    </div>
  );
}
